/**
 * Determines and sets the foreground color of an element based on its background color.
 *
 * @returns The determined foreground color.
 */
export function setForegroundColor({
  element,
  threshold = 0.4,
  lightColor = '#fff',
  darkColor = '#000',
}: {
  /** Element to get the background color from */
  element: HTMLElement;
  /** Threshold to determine if the foreground color should be black or white. Default is 0.4. */
  threshold?: number;
  /** Light color to be used if the background color is dark. Default is '#fff' */
  lightColor?: string;
  /** Dark color to be used if the background color is light. Default is '#000' */
  darkColor?: string;
}) {
  if (!element) {
    throw new Error('Element is required');
  }

  const styles = getComputedStyle(element);
  const backgroundColor = styles.backgroundColor;
  const luminance = calculateLuminance(backgroundColor);
  const color = luminance > threshold ? darkColor : lightColor;

  element.style.setProperty('--foreground-color', color);

  return color;
}

/**
 * Calculates the luminance of an RGB color.
 *
 * @param color - A string representing the RGB color in the format "rgb(r, g, b)".
 * @returns The luminance of the color as a number between 0 and 1.
 * @throws Will throw an error if the color format is invalid.
 * @see https://css-tricks.com/switch-font-color-for-different-backgrounds-with-css/
 * @see https://scm.lup.ie/engineering/LearnUpon/-/blob/main/client/main_app/stylesheets/lu-theme.scss.erb#L49
 */
export function calculateLuminance(color: string): number {
  const RGB_MAX = 255;
  const LUMINANCE_R = 0.2126;
  const LUMINANCE_G = 0.7152;
  const LUMINANCE_B = 0.0722;
  const THRESHOLD = 0.03928;
  const GAMMA = 2.4;
  const SCALE_FACTOR = 12.92;
  const OFFSET = 0.055;
  const NORMALIZE_FACTOR = 1.055;

  const rgb = color.match(/\d+/g)?.map(Number);
  if (!rgb || rgb.length < 3) {
    return 0;
  }

  const [r, g, b] = rgb.map((channelValue) => {
    const normalizedValue = channelValue / RGB_MAX;
    if (normalizedValue <= THRESHOLD) {
      return normalizedValue / SCALE_FACTOR;
    } else {
      return Math.pow((normalizedValue + OFFSET) / NORMALIZE_FACTOR, GAMMA);
    }
  });

  return LUMINANCE_R * r + LUMINANCE_G * g + LUMINANCE_B * b;
}
